/* OKKO B2B — one AJAX path for everything above the table: filters, chips, the
 * availability toggle, sort, numbered pagination AND the search bar.
 *
 * Strategy: fetch the target URL as a full page and swap the whole
 * [data-okko-collection] container. Collection and search pages share the same
 * markup (okko-catalog snippet), so navigating between them is seamless. Every
 * JS listener in this theme is delegated on `document`, which is what makes the
 * container swap safe — nothing holds a reference into the old subtree.
 */

(() => {
  'use strict';

  const container = () => document.querySelector('[data-okko-collection]');

  let inFlight = null;

  async function render(url, { push = true, scroll = false, keepSearch = true } = {}) {
    const root = container();
    if (!root) return;

    if (inFlight) inFlight.abort();
    inFlight = new AbortController();
    root.setAttribute('aria-busy', 'true');

    try {
      const res = await fetch(url, { signal: inFlight.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('[data-okko-collection]');
      if (!fresh) throw new Error('risposta senza [data-okko-collection]');

      // Preserve the search input state across the swap: while typing, the swap
      // must not steal the caret.
      const oldInput = root.querySelector('[data-okko-search] input[name="q"]');
      const hadFocus = keepSearch && oldInput && document.activeElement === oldInput;
      const oldValue = oldInput ? oldInput.value : null;
      const caret = oldInput ? oldInput.selectionStart : 0;

      // Preserve which sidebar selects are open (and their inner search term):
      // checking a checkbox triggers this swap, and without this the dropdown the
      // user is still selecting in would snap shut on every click.
      const openSelects = [...root.querySelectorAll('[data-okko-select][open]')].map((d) => ({
        key: d.dataset.okkoSelect,
        term: d.querySelector('[data-okko-filter-search]')?.value || '',
      }));

      root.replaceWith(fresh);

      openSelects.forEach(({ key, term }) => {
        const d = fresh.querySelector(`[data-okko-select="${key}"]`);
        if (!d) return;
        d.open = true;
        const si = d.querySelector('[data-okko-filter-search]');
        if (si && term) {
          si.value = term;
          applyOptionFilter(si);
        }
      });

      // Ridipinge le quantita' della bozza sulle righe appena arrivate: e' questo
      // che rende l'ordine persistente attraverso paginazione, filtri e ricerca.
      if (window.OKKO && window.OKKO.restoreDraft) window.OKKO.restoreDraft();
      if (window.OKKO && window.OKKO.paintWishlist) window.OKKO.paintWishlist();
      if (window.OKKO && window.OKKO.paintBis) window.OKKO.paintBis();
      // parole cercate evidenziate sui risultati appena arrivati (okko-evidenzia.js)
      if (window.OKKO && window.OKKO.evidenzia) window.OKKO.evidenzia();
      if (push) window.history.pushState({ okko: true }, '', url);
      syncActiveFilters();
      collocaControlli();

      if (hadFocus) {
        const input = fresh.querySelector('[data-okko-search] input[name="q"]');
        if (input) {
          input.value = oldValue;
          input.focus();
          input.setSelectionRange(caret, caret);
        }
      }
      // La barra sta nella TESTATA, fuori dal contenitore sostituito: qui si
      // allinea il campo all'URL appena reso (X dopo una ricerca dal catalogo,
      // campo vuoto dopo la X, termine giusto al back del browser). Se si sta
      // ancora digitando il campo non si tocca.
      syncSearchBar(new URL(url, window.location.href).searchParams.get('q') || '');
      if (scroll) fresh.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[okko-facets]', err);
        window.location.href = url; // fall back to a real navigation rather than a dead page
      }
    } finally {
      const r = container();
      if (r) r.removeAttribute('aria-busy');
      inFlight = null;
    }
  }

  function urlFromForm(form) {
    // FormData includes the inputs associated via the form="FacetsForm" attribute,
    // scattered across chips, toggle, sidebar and sort. A filter can exist both as a
    // chip and as a sidebar checkbox (same name+value, kept in sync), so identical
    // pairs are deduped or the URL would carry every filter twice.
    const seen = new Set();
    const params = new URLSearchParams();
    for (const [k, v] of new FormData(form)) {
      if (v === '') continue;
      const pair = `${k}=${v}`;
      if (seen.has(pair)) continue;
      seen.add(pair);
      params.append(k, v);
    }
    const qs = params.toString();
    return form.getAttribute('action') + (qs ? `?${qs}` : '');
  }

  // --- Filtri / toggle / sort / chips ---------------------------------------

  document.addEventListener('change', (e) => {
    const form = e.target.form || e.target.closest('form');
    if (!form || !form.matches('[data-okko-facets]')) return;
    // Lo stesso filtro puo' comparire due volte (chip in alto + checkbox in sidebar,
    // stesso name+value). Senza allineare i gemelli PRIMA di leggere il form, togliere
    // la spunta da uno lascia l'altro attivo e il filtro non si disattiva mai.
    if (e.target.type === 'checkbox') {
      document
        .querySelectorAll(`input[type="checkbox"][name="${CSS.escape(e.target.name)}"]`)
        .forEach((twin) => {
          if (twin !== e.target && twin.value === e.target.value) twin.checked = e.target.checked;
        });
    }
    render(urlFromForm(form));
  });

  // --- Ricerca dentro un filtro a select ------------------------------------

  // Ricerca dentro un pannello filtro. NON e' un "contiene" secco: si cercano
  // TUTTE le parole digitate, in qualunque ordine e non per forza attaccate,
  // cosi' "Motorola G" trova "MOTOROLA MOTO G06 4G" anche se in mezzo c'e'
  // "MOTO" (richiesta di Nicolas, 2026-08-05: i nomi dei modelli hanno pezzi
  // che nessuno digita).
  //
  // Le parole di UNA lettera sola devono pero' iniziare una parola, non valere
  // ovunque: altrimenti la "g" di "Motorola G" tirerebbe dentro anche
  // "MOTOROLA EDGE 70", per la g in mezzo a "edge". Da due caratteri in su
  // vale il contenimento normale, cosi' "24" trova "S24" e "old" trova "FOLD".
  function applyOptionFilter(input) {
    const parole = input.value.trim().toLowerCase().split(/\s+/).filter(Boolean);
    input.closest('.okko-select__panel').querySelectorAll('.okko-sidebar__opt').forEach((opt) => {
      // solo l'ETICHETTA: opt.textContent si porta dietro anche il conteggio,
      // e cercando "3" sarebbero saltate fuori le voci che hanno 3 prodotti
      const testo = (opt.querySelector('.okko-sidebar__optlabel') || opt).textContent.toLowerCase();
      const pezzi = testo.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
      const trovata = (p) => (p.length > 1 ? testo.includes(p) : pezzi.some((x) => x.startsWith(p)));
      opt.hidden = parole.length > 0 && !parole.every(trovata);
    });
  }

  document.addEventListener('input', (e) => {
    if (e.target.matches('[data-okko-filter-search]')) applyOptionFilter(e.target);
  });

  // --- Ricerca ---------------------------------------------------------------

  const MIN_CHARS = 2;
  let timer = null;

  function searchUrl(form) {
    const q = form.querySelector('input[name="q"]').value.trim();
    if (!q) return container().dataset.okkoHome || form.getAttribute('action');
    return `${form.getAttribute('action')}?type=product&q=${encodeURIComponent(q)}`;
  }

  /** X visibile se il campo ha testo, suggerimento se e' vuoto. Con `valore`
   *  (stringa) si imposta prima il campo, ma solo se non ha il focus: chi
   *  digita non va interrotto. La barra e' una sola, nella testata. */
  function syncSearchBar(valore) {
    const form = document.querySelector('[data-okko-search]');
    if (!form) return;
    const input = form.querySelector('input[name="q"]');
    if (typeof valore === 'string' && document.activeElement !== input) input.value = valore;
    const pieno = input.value.trim().length > 0;
    const clear = form.querySelector('[data-okko-search-clear]');
    const hint = form.querySelector('[data-okko-search-hint]');
    if (clear) clear.hidden = !pieno;
    if (hint) hint.hidden = pieno;
  }

  document.addEventListener('input', (e) => {
    const form = e.target.closest('[data-okko-search]');
    if (!form || e.target.name !== 'q') return;
    syncSearchBar(); // X / suggerimento seguono il campo su OGNI pagina
    // Barra nella testata: fuori dalle pagine catalogo niente ricerca live
    if (!container()) return;
    clearTimeout(timer);
    const q = e.target.value.trim();
    if (q.length !== 0 && q.length < MIN_CHARS) return;
    timer = setTimeout(() => render(searchUrl(form)), 350);
  });

  document.addEventListener('submit', (e) => {
    const searchForm = e.target.closest('[data-okko-search]');
    if (searchForm) {
      // Fuori dal catalogo non c'e' nulla da swappare: submit nativo verso /search
      if (!container()) return;
      e.preventDefault();
      clearTimeout(timer);
      render(searchUrl(searchForm));
      return;
    }
    if (e.target.matches('[data-okko-facets]')) e.preventDefault();
  });

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-okko-search-clear]')) {
      // La X svuota il CAMPO (che sta nella testata e lo swap non tocca), poi
      // riporta al catalogo intero. Fuori dal catalogo (home) svuota e basta.
      const form = e.target.closest('[data-okko-search]');
      const input = form && form.querySelector('input[name="q"]');
      if (input) { input.value = ''; input.focus(); }
      syncSearchBar();
      clearTimeout(timer);
      const root = container();
      if (!root) return;
      // keepSearch:false — il clear NON deve far riapparire la query nel campo nuovo
      render(root.dataset.okkoHome || '/collections/all', { keepSearch: false });
      return;
    }
    // [data-okko-nav-link]: navigazione interna al catalogo (es. select Categorie)
    const link = e.target.closest('.okko-page-link, .okko-facets__pill, [data-okko-nav-link]');
    if (!link || e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    render(link.href, { scroll: link.classList.contains('okko-page-link') });
  });

  window.addEventListener('popstate', () => render(window.location.href, { push: false }));

  // --- Sonda del filtro Modelli ---------------------------------------------
  // Su un vocabolario grosso (610 modelli distinti) Shopify dichiara solo un
  // SOTTOINSIEME dei valori, e piccolo: misurato su TPU TRASPARENTI + SAMSUNG,
  // 6 valori contro i 68 veri del result set; con marca=WIKO la lista tornava
  // proprio vuota. La regola con cui sceglie non e' documentata (non e' il
  // banale "primi 100 in alfabetico": fra i 6 c'erano S22 e S25). All'apertura
  // del select, ?view=modelli restituisce i metafield del result set corrente e
  // le opzioni vengono ricostruite da li': complete e coerenti coi filtri attivi.

  const probeCache = new Map();
  const PROBE_MAX_PAGES = 8; // oltre ~2000 prodotti filtrati teniamo la lista nativa

  function probeUrl(page) {
    const params = new URLSearchParams(window.location.search);
    const modelloAttivo = [...params.keys()].some((k) => k.includes('.modelli'));
    [...params.keys()]
      .filter((k) => k.includes('.modelli') || k === 'page' || k === 'view')
      .forEach((k) => params.delete(k));
    // Il filtro modelli va tolto dalla sonda (multi-select: le alternative
    // devono restare visibili), ma senza altri vincoli la lista tornava
    // GLOBALE. Con un modello attivo e nessuna marca attiva, la sonda si
    // vincola alle marche RIMASTE nel result set (le opzioni del select
    // Marca, server-rendered sul filtrato): "modelli della marca", come
    // chiesto da Nicolas (2026-08-03).
    if (modelloAttivo && ![...params.keys()].some((k) => k.includes('.marca'))) {
      const marca = document.querySelector('[data-okko-facet-param*=".marca"]');
      if (marca) {
        const p = marca.dataset.okkoFacetParam;
        marca.querySelectorAll('input[type="checkbox"]').forEach((i) => params.append(p, i.value));
      }
    }
    params.set('view', 'modelli');
    if (page > 1) params.set('page', String(page));
    return `${window.location.pathname}?${params.toString()}`;
  }

  async function fetchProbe() {
    const key = probeUrl(1);
    if (probeCache.has(key)) return probeCache.get(key);
    const first = JSON.parse(await (await fetch(key)).text());
    if (first.pages > PROBE_MAX_PAGES) return null;
    const rest = [];
    for (let page = 2; page <= first.pages; page += 1) {
      rest.push(fetch(probeUrl(page)).then((r) => r.text()).then(JSON.parse));
    }
    const chunks = [first, ...(await Promise.all(rest))];
    const counts = new Map();
    chunks.forEach((chunk) => chunk.models.forEach((list) => {
      (list || []).forEach((m) => counts.set(m, (counts.get(m) || 0) + 1));
    }));
    probeCache.set(key, counts);
    return counts;
  }

  const escapeHtml = (str) => str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');

  // --- Riconciliazione dei filtri attivi con l'URL ---------------------------
  // filter.active_values di Shopify perde i valori oltre il tetto dei 100 del
  // vocabolario globale (stessa trappola della lista troncata): il server APPLICA
  // il filtro ma non lo dichiara — con solo un modello attivo la fascia pillole
  // non compariva e la spunta in sidebar restava vuota. Qui pillole, spunte e
  // conteggi (n) si ricostruiscono dai parametri REALI dell'URL dopo ogni render.

  function syncActiveFilters() {
    const root = container();
    if (!root) return;
    const attivi = [...new URLSearchParams(window.location.search).entries()]
      .filter(([k, v]) => k.startsWith('filter.') && v !== '');

    // spunte: i valori oltre il tetto non hanno checkbox finche' la sonda non
    // ricostruisce la lista, ma quelle presenti vanno allineate
    attivi.forEach(([k, v]) => {
      root.querySelectorAll(`input[type="checkbox"][name="${CSS.escape(k)}"]`).forEach((i) => {
        if (i.value === v) i.checked = true;
      });
    });

    // URL di rimozione: servono sia alla fascia in resultsbar sia alle pillole
    // dentro i pannelli qui sotto
    const urlCon = (coppie) => {
      const qs = new URLSearchParams(coppie).toString();
      return window.location.pathname + (qs ? `?${qs}` : '');
    };
    const tutte = [...new URLSearchParams(window.location.search).entries()];
    const urlSenza = (chiave, valore) =>
      urlCon(tutte.filter(([k, v]) => !(k === chiave && v === valore) && k !== 'page'));
    const urlPulito = urlCon(tutte.filter(([k]) => !k.startsWith('filter.') && k !== 'page'));

    // Per ogni select: conteggio a PILLOLA sul summary (sempre nel DOM, hidden
    // a 0) + is-filtrato, e i valori ATTIVI come pillole DENTRO il pannello,
    // sotto la ricerca (richiesta di Nicolas 2026-08-03: dopo il rebuild della
    // sonda il valore selezionato non si vedeva senza scrollare la lista).
    // La × delle pillole e' un .okko-facets__pill: la intercetta il click
    // delegato di render(), e il pannello riapre via openSelects.
    root.querySelectorAll('[data-okko-select]').forEach((det) => {
      const p = det.dataset.okkoFacetParam;
      const pill = det.querySelector('summary .okko-select__count');
      if (!p || !pill) return;
      const valori = attivi.filter(([k]) => k === p).map(([, v]) => v);
      pill.textContent = valori.length;
      pill.hidden = valori.length === 0;
      det.classList.toggle('is-filtrato', valori.length > 0);
      let strip = det.querySelector('[data-okko-select-attivi]');
      if (!valori.length) {
        if (strip) strip.remove();
        return;
      }
      if (!strip) {
        strip = document.createElement('div');
        strip.className = 'okko-select__attivi';
        strip.setAttribute('data-okko-select-attivi', '');
        const panel = det.querySelector('.okko-select__panel');
        const search = panel && panel.querySelector('[data-okko-filter-search]');
        if (search) search.after(strip);
        else if (panel) panel.prepend(strip);
        else return;
      }
      strip.innerHTML = valori
        .map((v) => `<a class="okko-facets__pill okko-facets__pill--mini" href="${escapeHtml(urlSenza(p, v))}">${escapeHtml(v)} <span aria-hidden="true">&times;</span></a>`)
        .join('');
    });

    const vecchia = root.querySelector('.okko-active-pills');
    if (attivi.length === 0) {
      if (vecchia) vecchia.remove();
      return;
    }
    const T = (window.OKKO && window.OKKO.testi) || {};
    const etichetta = (k, v) => {
      if (k.includes('availability')) return T.disponibiliFiltro || 'Disponibili';
      if (k.endsWith('.gte')) return `${T.prezzoDa || 'Prezzo da'} ${v}€`;
      if (k.endsWith('.lte')) return `${T.prezzoFino || 'Prezzo fino a'} ${v}€`;
      return v;
    };

    const banda = document.createElement('div');
    banda.className = 'okko-active-pills';
    banda.innerHTML = attivi
      .map(([k, v]) => `<a class="okko-facets__pill" href="${escapeHtml(urlSenza(k, v))}">${escapeHtml(etichetta(k, v))} <span aria-hidden="true">&times;</span></a>`)
      .join('')
      + `<a class="okko-facets__pill okko-clear-filters" href="${escapeHtml(urlPulito)}">${T.rimuoviFiltri || 'Rimuovi filtri'}</a>`;

    if (vecchia) vecchia.remove();
    // le pillole vivono DENTRO la resultsbar, tra il toggle e l'ordinamento
    const resultsbar = root.querySelector('.okko-resultsbar');
    if (!resultsbar) return;
    const sort = resultsbar.querySelector('.okko-resultsbar__sort');
    if (sort) resultsbar.insertBefore(banda, sort);
    else resultsbar.appendChild(banda);
  }

  // Accordion in sidebar: all'apertura di un select si chiudono gli altri
  // (richiesta di Nicolas 2026-08-03). Chiudere via `open = false` rifira un
  // toggle, che pero' il guard `!det.open` scarta. `toggle` non bolle: cattura.
  document.addEventListener('toggle', (e) => {
    const det = e.target;
    if (!(det instanceof Element) || !det.open || !det.matches('[data-okko-select]')) return;
    const aside = det.closest('.okko-sidebar');
    if (!aside) return;
    aside.querySelectorAll('[data-okko-select][open]').forEach((d) => {
      if (d !== det) d.open = false;
    });
  }, true);

  // `toggle` non bolle: serve la fase di cattura.
  document.addEventListener('toggle', async (e) => {
    const det = e.target;
    if (!(det instanceof Element)) return;
    if (det.dataset.okkoSelect !== 'modelli' || !det.open) return;
    if (!window.location.pathname.includes('/collections/')) return; // la vista sonda esiste solo li'

    const paramName = det.dataset.okkoFacetParam;
    const wrap = det.querySelector('.okko-select__opts');
    if (!paramName || !wrap) return;

    let counts = null;
    try { counts = await fetchProbe(); } catch { /* sonda ko: resta la lista nativa */ }
    if (!counts || !det.open) return;

    const active = new Set(new URLSearchParams(window.location.search).getAll(paramName));
    active.forEach((v) => { if (!counts.has(v)) counts.set(v, 0); }); // un attivo resta deselezionabile

    wrap.innerHTML = [...counts.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], 'it'))
      .map(([value, count]) => `<label class="okko-sidebar__opt">
        <input type="checkbox" form="FacetsForm" name="${escapeHtml(paramName)}" value="${escapeHtml(value)}" ${active.has(value) ? 'checked' : ''}>
        <span class="okko-sidebar__optlabel">${escapeHtml(value)}</span>
        <span class="okko-sidebar__optcount">${count}</span>
      </label>`)
      .join('');

    const si = det.querySelector('[data-okko-filter-search]');
    if (si && si.value) applyOptionFilter(si);
  }, true);

  // --- Mobile: toggle disponibili e ordinamento nel pannello filtri ----------
  // La resultsbar mobile tiene solo titolo+conteggio+Filtri (+pillole): i due
  // controlli traslocano nello slot in cima alla sidebar. Sono gli STESSI nodi
  // (listener delegati su document: lo spostamento e' sicuro), quindi niente
  // stato da sincronizzare. Ogni swap fa rinascere il DOM nella posizione
  // Liquid: si ricolloca dopo ogni render (e al cambio di breakpoint).

  const mqPannello = window.matchMedia('(max-width: 640px)');

  function collocaControlli() {
    const root = container();
    if (!root) return;
    const slot = root.querySelector('[data-okko-controlli-slot]');
    const bar = root.querySelector('.okko-resultsbar');
    if (!slot || !bar) return;
    const sw = root.querySelector('.okko-switch');
    const sort = root.querySelector('.okko-resultsbar__sort');
    if (mqPannello.matches) {
      if (sw) slot.appendChild(sw);
      if (sort) slot.appendChild(sort);
    } else {
      // ordine originale nella barra: … filtri, toggle, pillole, sort in coda
      if (sw) bar.insertBefore(sw, bar.querySelector('.okko-active-pills'));
      if (sort) bar.appendChild(sort);
    }
  }

  mqPannello.addEventListener('change', collocaControlli);

  // Primo allineamento al load (lo script e' defer: il DOM e' gia' pronto)
  syncActiveFilters();
  collocaControlli();
})();
